import { AddressModel } from '../models';
import { isEmpty, getDefaultValue, getConditionalDefaultValue } from './generalUtils';
import { CountryCode } from '../constants';

export const canadaRegex = new RegExp(/^[a-zA-Z]{1}[0-9]{1}[a-zA-Z]{1}-[0-9]{1}[a-zA-Z]{1}[0-9]{1}$/);
export const zipRegex = new RegExp(/^[0-9]{5}-[0-9]{4}$|^[0-9]{5}$/);

export const formatZipCode = (postalCode: string) => {
  return postalCode?.trim()?.replace(' ', '-');
};

export const sanitizeZipCode = (zipCode: string) => {
  return getConditionalDefaultValue(zipCode?.length === 6, zipCode?.replace('-', '').replace(' ', ''), zipCode);
};

export const sanitizeAddress = (address: AddressModel.IAddress) => ({
  ...address,
  line1: !isEmpty(address.line1) ? address.line1 : null,
  line2: !isEmpty(address.line2) ? address.line2 : null,
  stateCode: !isEmpty(address.stateCode) ? address.stateCode : null,
  stateName: !isEmpty(address.stateName) ? address.stateName : null,
  countryId: getConditionalDefaultValue(
    address?.countryId,
    address?.countryId,
    getDefaultValue(address?.country?.id, null),
  ),
  city: !isEmpty(address.city) ? address.city : null,
  zipCode: !isEmpty(address.zipCode) ? sanitizeZipCode(address.zipCode) : null,
});

export const isNYState = (stateCode: string) => stateCode === 'NY';

// State codes are not unique across countries (e.g. CO = Colorado, US / Coahuila, MX), so match on country too
export const findStateByCodeAndCountry = (states: any[], stateCode: string, country: any) =>
  country
    ? states.find(
        (s: any) =>
          s.code === stateCode && (s.country === country.name || s.country === country.id || s.country === country.code),
      ) || null
    : null;

export const isNYCity = (city: string) => city === 'New York';

export const getLocationString = (city: string, stateName: string) =>
  isEmpty(city) && isEmpty(stateName)
    ? '-'
    : `${city ?? ''}${city && stateName ? ', ' : ''}${stateName ? stateName : ''}`;

export const getAddressString = (address: AddressModel.IAddress) =>
  address ? `${address.line1 ? `${address.line1}, ` : ''}${getLocationString(address.city, address?.stateName)}` : '-';

export const getZipCodeMaskByCountry = (
  countryCode: CountryCode = CountryCode.UNITED_STATES,
  zipCode: string,
): { placeholder: string; mask: any[] } => {
  const zipCodeMap = {
    [CountryCode.UNITED_STATES]: getConditionalDefaultValue(
      zipCode?.length > 5,
      AddressModel.zipCodeFormatMap[AddressModel.ZipCodeFormatType.FIVE_DIGITS_PLUS_FOUR],
      AddressModel.zipCodeFormatMap[AddressModel.ZipCodeFormatType.FIVE_DIGITS],
    ),
    [CountryCode.PUERTO_RICO]: getConditionalDefaultValue(
      zipCode?.length > 5,
      AddressModel.zipCodeFormatMap[AddressModel.ZipCodeFormatType.FIVE_DIGITS_PLUS_FOUR],
      AddressModel.zipCodeFormatMap[AddressModel.ZipCodeFormatType.FIVE_DIGITS],
    ),
    [CountryCode.MEXICO]: AddressModel.zipCodeFormatMap[AddressModel.ZipCodeFormatType.FIVE_DIGITS],
    [CountryCode.CANADA]: AddressModel.zipCodeFormatMap[AddressModel.ZipCodeFormatType.CANADA_FORMAT],
  };
  return zipCodeMap[countryCode];
};

// Helper function to format address object into a readable string
export const formatAddress = (address: any): string => {
  if (!address) return '';

  const parts = [];

  if (address.line1) {
    parts.push(address.line1);
  }

  if (address.line2) {
    parts.push(address.line2);
  }

  if (address.city) {
    parts.push(address.city);
  }

  if (address.stateName) {
    parts.push(address.stateName);
  }

  if (address?.country?.name) {
    parts.push(address?.country?.name);
  }

  if (address.county) {
    parts.push(address.county);
  }

  if (address.zipCode) {
    parts.push(address.zipCode);
  }

  return parts.join(', \n') || '';
};
